/**
 * Koneko B canonical task feed (frontend-local starter).
 *
 * Goal:
 * - one shape for tasks from GitLab + Asana
 * - one merged list for UI
 * - easy to swap mock adapters for API adapters later
 */

import {
  GITLAB_STYLE_PRIORITY_LABELS,
  managementFireLevel,
  mergeGitLabStylePriorityIntoLabels,
  scoreManagementBot,
  taskHasExplicitPriorityLabel,
} from "./managementBotScore";

function nowIso() {
  return new Date().toISOString();
}

/** @param {string|null|undefined} md */
function gitlabDescriptionToPlain(md) {
  const s = String(md || "").trim();
  if (!s) return null;
  return String(s)
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Read embedded NekoHR / manual estimates from task title or notes, e.g. `[Est: 2h]`, `(Est: 90m)`.
 * @param {string} title
 * @param {string} notesPlain
 * @returns {number|null} minutes
 */
export function parseEmbeddedEstimateMinutes(title, notesPlain) {
  const text = `${String(title || "").trim()}\n${String(
    notesPlain || ""
  ).trim()}`;
  const patterns = [
    /\[Est:\s*([0-9]+(?:\.[0-9]+)?)\s*(h(?:ours?)?|m(?:ins?|inutes?)?)?\]/i,
    /\(\s*Est:\s*([0-9]+(?:\.[0-9]+)?)\s*(h(?:ours?)?|m(?:ins?|inutes?)?)?\s*\)/i,
  ];
  for (let i = 0; i < patterns.length; i += 1) {
    const m = text.match(patterns[i]);
    if (!m) continue;
    const num = Number(m[1]);
    if (!Number.isFinite(num) || num < 0) continue;
    const u = String(m[2] || "h").toLowerCase();
    if (u.startsWith("m")) return Math.round(num);
    return Math.round(num * 60);
  }
  return null;
}

export function mergeEstimateIntoAsanaNotes(notesPlain, hours) {
  const h = Number(hours);
  if (!Number.isFinite(h) || h < 0) return String(notesPlain || "");
  const label = `(Est: ${h}h)`;
  const raw = String(notesPlain || "");
  if (/\(\s*Est:\s*[^\n)]+\)/i.test(raw)) {
    return raw.replace(/\(\s*Est:\s*[^\n)]+\)/i, label);
  }
  const t = raw.trim();
  return t ? `${t}\n\n${label}` : label;
}

/**
 * Strip `[Est: …]` from an Asana task title so we can optionally re-append after editing.
 * @param {string} name
 */
export function stripEmbeddedEstimateFromTitle(name) {
  return String(name || "")
    .replace(/\[Est:\s*[^\]]+\]\s*/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

const MBOT_BASE = "https://management-bot-api.herokuapp.com";
const ASANA_BASE = "https://app.asana.com/api/1.0";
const ASANA_CONFIG_KEY = "konekoB:asanaConfig";
const GITLAB_BASE_DEFAULT = "https://nekogit.nekoapi.xyz";

const ASANA_PROJECT_ALIASES = {
  nekoadmin: "NekoAdmin",
  nekobizdev: "NekoBizDev",
  nekorecruit: "NekoRecruit",
  nekohr: "NekoHR",
  nekosocials: "NekoSocials",
  nekocrm: "NekoCRM",
  nekoops: "NekoOps",
  nekolegal: "NekoLegal",
};

function normalizeAliasKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function canonicalizeAsanaProjectName(name) {
  const raw = String(name || "").trim();
  if (!raw) return "Asana";
  const alias = ASANA_PROJECT_ALIASES[normalizeAliasKey(raw)];
  return alias || raw;
}

export function isKnownAsanaProjectName(name) {
  return !!ASANA_PROJECT_ALIASES[normalizeAliasKey(name)];
}

function parseProjectGids(value) {
  return String(value || "")
    .split(/[\n,\s]+/)
    .map((v) => v.trim())
    .filter(Boolean);
}

function withAsanaHeaders(token) {
  return {Authorization: `Bearer ${token}`};
}

/**
 * @typedef {Object} CanonicalTask
 * @property {"gitlab"|"asana"} source
 * @property {string} sourceTaskId
 * @property {string} externalUrl
 * @property {string} title
 * @property {string} assigneeKey
 * @property {string} assigneeLabel
 * @property {string} projectKey
 * @property {string} projectLabel
 * @property {string} status
 * @property {number|null} estimateMinutes
 * @property {number|null} spentMinutes
 * @property {string|null} dueDate
 * @property {string|null} [milestoneDueDate] — GitLab milestone due date (YYYY-MM-DD), optional
 * @property {string} updatedAt
 * @property {string[]} labels — GitLab label titles and/or Asana tag names for NEEDS_* derivation
 * @property {string|null} [description] — plain-text body for UI (issue description / Asana notes)
 * @property {{gid: string, name: string}[]} [asanaTags] — when `source === "asana"`, tags with gid for API updates
 */

/** Management-bot-aligned GitLab labels (see app/summarizer.py) */
export const KONEKO_KNOWN_CYCLE_LABELS = {
  NEEDS_DISCUSSION: "Cycle: In Discussion",
  NEEDS_SUBTASKING: "Tag: Needs Subtasking",
  NEEDS_MORE_INFO: "Tag: Needs More Info",
  BLOCKED: "Cycle: Blocked",
};

/**
 * Stable pseudo-random score in ~[0.5, 9.9] until real scoring lands.
 * @param {string} stableKey
 * @returns {number}
 */
export function placeholderTaskScore(stableKey) {
  const s = String(stableKey || "x");
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  const u = (Math.abs(h) % 10000) / 10000;
  return Math.round((0.5 + u * 9.4) * 10) / 10;
}

/**
 * Urgency 0–4 (🔥 count) from due date alone (management-bot ties fire to milestones + schedule; we approximate).
 * @param {string|null} dueDate — YYYY-MM-DD
 * @returns {number}
 */
export function fireLevelFromDueDate(dueDate) {
  if (!dueDate) return 0;
  const raw = String(dueDate).trim();
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return 0;
  const endUtc = Date.UTC(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    23,
    59,
    59,
    999
  );
  const now = Date.now();
  const msPerDay = 86400000;
  const diffDays = Math.ceil((endUtc - now) / msPerDay);
  if (diffDays < 0) return 4;
  if (diffDays <= 3) return 4;
  if (diffDays <= 7) return 3;
  if (diffDays <= 14) return 2;
  if (diffDays <= 30) return 1;
  return 0;
}

/**
 * Tool tags for filters, Slack/deep-links, and manager nudges.
 * @param {CanonicalTask & {labels?: string[]}} task
 * @returns {string[]}
 */
export function deriveKonekoTools(task) {
  const labels = Array.isArray(task.labels)
    ? task.labels.map((x) => String(x))
    : [];
  const has = (exact) => labels.includes(exact);

  /** @type {string[]} */
  const tools = [];

  const unassigned =
    !task.assigneeKey ||
    task.assigneeKey === "unknown" ||
    String(task.assigneeLabel || "")
      .toLowerCase()
      .includes("not assigned");

  if (unassigned) tools.push("UNASSIGNED");

  const est = task.estimateMinutes;
  if (!Number.isFinite(est) || est <= 0) tools.push("NEEDS_ESTIMATE");

  const hasAnyDue =
    Boolean(String(task.dueDate || "").trim()) ||
    Boolean(String(task.milestoneDueDate || "").trim());
  if (!hasAnyDue) tools.push("NEEDS_DUE_DATE");

  if (!taskHasExplicitPriorityLabel(labels)) tools.push("NEEDS_PRIORITY");

  if (has(KONEKO_KNOWN_CYCLE_LABELS.NEEDS_SUBTASKING))
    tools.push("NEEDS_SUBTASKING");
  if (has(KONEKO_KNOWN_CYCLE_LABELS.NEEDS_MORE_INFO))
    tools.push("NEEDS_MORE_INFO");
  if (has(KONEKO_KNOWN_CYCLE_LABELS.NEEDS_DISCUSSION))
    tools.push("NEEDS_DISCUSSION");
  if (has(KONEKO_KNOWN_CYCLE_LABELS.BLOCKED)) tools.push("BLOCKED");

  return tools;
}

/**
 * Combined row for UI sorting (management-bot-style score + fire).
 * @param {CanonicalTask & {labels?: string[]}} task
 */
export function enrichTaskPresentation(task) {
  const tools = deriveKonekoTools(task);
  const managementScore = scoreManagementBot(task);
  const managementFire = managementFireLevel(task);
  return {
    ...task,
    presentationScore: managementScore,
    managementScore,
    managementFire,
    urgencyLevel: managementFire,
    tools,
  };
}

/** @param {ReturnType<enrichTaskPresentation>[]} enriched @param {'score'|'fire'|'updated'} [sortBy] */
export function sortTasksPresentationOrder(enriched, sortBy = "score") {
  const copy = [...enriched];
  const mode = sortBy === "fire" || sortBy === "updated" ? sortBy : "score";
  copy.sort((a, b) => {
    if (mode === "updated") {
      const du = String(b.updatedAt).localeCompare(String(a.updatedAt));
      if (du !== 0) return du;
    } else if (mode === "fire") {
      const df = Number(b.managementFire) - Number(a.managementFire);
      if (df !== 0) return df;
    } else {
      const ds = Number(b.presentationScore) - Number(a.presentationScore);
      if (ds !== 0) return ds;
      const df = Number(b.managementFire) - Number(a.managementFire);
      if (df !== 0) return df;
    }
    const ds = Number(b.presentationScore) - Number(a.presentationScore);
    if (ds !== 0) return ds;
    const df = Number(b.managementFire) - Number(a.managementFire);
    if (df !== 0) return df;
    return String(b.updatedAt).localeCompare(String(a.updatedAt));
  });
  return copy;
}

export function summarizeTools(tasks) {
  /** @type {Record<string, number>} */
  const counts = {};
  (tasks || []).forEach((t) => {
    (t.tools || []).forEach((id) => {
      counts[id] = (counts[id] || 0) + 1;
    });
  });
  return counts;
}

/** @param {string} s */
function normalizeFieldKey(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/** @param {string} disp */
function parseHumanDurationToMinutes(disp) {
  const s = String(disp || "").trim();
  if (!s) return null;
  const m = s.match(/^(\d+(?:\.\d+)?)\s*(hours?|hrs?|h|minutes?|mins?|m)?$/i);
  if (!m) return null;
  const val = Number(m[1]);
  if (!Number.isFinite(val)) return null;
  const u = String(m[2] || "h").toLowerCase();
  if (u === "m" || u.startsWith("min")) return Math.round(val);
  return Math.round(val * 60);
}

/**
 * Prefer unit from field name ("minutes", "hours") then fallbackUnit ("hours"|"minutes").
 * @param {string} fieldName
 * @param {'hours'|'minutes'} fallbackUnit
 */
function inferStoredUnit(fieldName, fallbackUnit) {
  const n = normalizeFieldKey(fieldName);
  if (/\bmin(ute)s?\b|\bmins\b/.test(n)) return "minutes";
  if (/\bhour?s?\b|\bhrs\b|\bhr\b/.test(n)) return "hours";
  return fallbackUnit;
}

/**
 * @param {object} cf — Asana custom field object embedded on task
 * @param {'hours'|'minutes'} configUnit — how to interpret bare numeric number_value
 * @returns {number|null}
 */
function customFieldValueToMinutes(cf, configUnit) {
  const dispParsed = parseHumanDurationToMinutes(cf.display_value);
  if (dispParsed != null) return dispParsed;
  const textParsed = parseHumanDurationToMinutes(cf.text_value);
  if (textParsed != null) return textParsed;

  const raw = cf.number_value;
  if (typeof raw !== "number" || !Number.isFinite(raw)) return null;

  const u = inferStoredUnit(cf.name || "", configUnit);
  return u === "minutes" ? Math.round(raw) : Math.round(raw * 60);
}

/**
 * @param {object} cf
 */
function looksLikeStoryPoints(cf) {
  const n = normalizeFieldKey(cf.name || "");
  return /\bstory\s*points?\b|\bpoints?\b\s*estimate\b/.test(n);
}

/**
 * @param {object} cf
 * @param {'estimate'|'spent'} role
 */
function autoEstimateNameScore(cf, role) {
  if (looksLikeStoryPoints(cf)) return -1;
  const n = normalizeFieldKey(cf.name || "");
  if (!n) return -1;

  /** @type {RegExp[]} */
  const estRes =
    role === "estimate"
      ? [/\bestimate\b/i, /\beffort\b/i, /\bforecast\b/i, /\btime\s*budget\b/i]
      : [];
  /** @type {RegExp[]} */
  const spentRes =
    role === "spent"
      ? [
          /\bspent\b/i,
          /\blogged\b/i,
          /\bactual\b/i,
          /\btime\s*spent\b/i,
          /\bhours\s*spent\b/i,
        ]
      : [];

  const regs = role === "estimate" ? estRes : spentRes;
  for (let i = 0; i < regs.length; i += 1) {
    if (regs[i].test(n)) return 50 - i;
  }
  return -1;
}

/**
 * @param {object[]} cfs
 * @param {'estimate'|'spent'} role
 * @param {string} gidHint
 * @param {string} nameHint
 * @param {'hours'|'minutes'} unit
 * @returns {number|null}
 */
function pickMinutesFromCustomFields(cfs, role, gidHint, nameHint, unit) {
  const fields = Array.isArray(cfs) ? cfs : [];
  const gid = String(gidHint || "").trim();
  const wanted = normalizeFieldKey(nameHint);

  /** @type {{ cf: object, rank: number }[]} */
  const ranked = [];

  for (const cf of fields) {
    if (!cf) continue;

    let rank = -1;

    if (gid && String(cf.gid || "") === gid) {
      rank = 1000;
    } else if (wanted) {
      const nn = normalizeFieldKey(cf.name || "");
      if (nn === wanted) rank = 800;
      else if (nn.includes(wanted) || wanted.includes(nn)) rank = 600;
    } else {
      rank = autoEstimateNameScore(cf, role);
    }

    const minutes = rank >= 0 ? customFieldValueToMinutes(cf, unit) : null;
    if (rank >= 0 && minutes != null && minutes >= 0) {
      ranked.push({cf, rank});
    }
  }

  if (!ranked.length) return null;
  ranked.sort((a, b) => b.rank - a.rank);
  return customFieldValueToMinutes(ranked[0].cf, unit);
}

/**
 * Reads Asana workload custom fields into canonical minutes (best-effort).
 * Configure field names/GIDs locally in Koneko B UI, or rely on fuzzy name matching.
 *
 * @param {object} rawTask — Task record from Asana list API including custom_fields
 * @param {ReturnType<readAsanaConfig>} cfg
 * @returns {{ estimateMinutes: number|null, spentMinutes: number|null }}
 */
export function workloadMinutesFromAsanaTask(rawTask, cfg) {
  const cfs = rawTask?.custom_fields;
  const est = pickMinutesFromCustomFields(
    cfs,
    "estimate",
    cfg.asanaEstimateFieldGid || "",
    cfg.asanaEstimateFieldName || "",
    cfg.asanaEstimateUnit === "minutes" ? "minutes" : "hours"
  );
  const spent = pickMinutesFromCustomFields(
    cfs,
    "spent",
    cfg.asanaSpentFieldGid || "",
    cfg.asanaSpentFieldName || "",
    cfg.asanaSpentUnit === "minutes" ? "minutes" : "hours"
  );
  return {
    estimateMinutes: est,
    spentMinutes: spent,
  };
}

function mapNekoHRUserToManagementBotMember(username) {
  const map = {
    liam_maneki: "Liam%20Conroy",
    martin_ogg: "Martin%20Ogg",
    pablo: "L%27homme%20Sage",
    reegesh: "Reegesh%20Maharjan",
    oskar: "Oskar%20Strom",
    aroni: "Aroni%20Hipperson",
    tsurutani: "Akinori%20Tsurutani",
    owen: "Owen%20Bennett",
    jacques: "Marsden%20Jacques",
  };
  return map[String(username || "").toLowerCase()] || null;
}

function parseIssueStatus(issue) {
  const score = Number(issue.score || 0);
  if (score >= 10) return "critical";
  if (score >= 5) return "in_progress";
  return "todo";
}

function mapManagementBotIssue(issue, assigneeKey, assigneeLabel) {
  const spentHours = Number(issue.time_spent_hours || 0);
  const estHours = Number(issue.time_estimate_hours || 0);
  const labelRows = Array.isArray(issue.gitlab_labels)
    ? issue.gitlab_labels
    : Array.isArray(issue.labels)
    ? issue.labels
    : [];
  return {
    source: "gitlab",
    sourceTaskId: `gitlab:${issue.project_id}:${issue.issue_iid}`,
    externalUrl: issue.url || "",
    title: issue.title || "Untitled issue",
    assigneeKey: assigneeKey || "unknown",
    assigneeLabel: assigneeLabel || assigneeKey || "Unknown",
    projectKey: String(issue.project_id || "unknown"),
    projectLabel: issue.project_name || "Unknown Project",
    labels: labelRows.map((l) => String(l)),
    status: parseIssueStatus(issue),
    estimateMinutes: Number.isFinite(estHours)
      ? Math.round(estHours * 60)
      : null,
    spentMinutes: Number.isFinite(spentHours)
      ? Math.round(spentHours * 60)
      : null,
    dueDate: null,
    milestoneDueDate:
      issue.milestone_date &&
      String(issue.milestone_date) !== "No Milestone Date"
        ? String(issue.milestone_date).slice(0, 10)
        : null,
    updatedAt: nowIso(),
    description: null,
  };
}

function getAsanaConfig() {
  try {
    const raw = localStorage.getItem(ASANA_CONFIG_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

/** Vue CLI exposes only keys prefixed with VUE_APP_; restart dev server after editing `.env`. */
function envPublic(key) {
  try {
    if (typeof process === "undefined" || !process.env) return "";
    return String(process.env[key] || "").trim();
  } catch {
    return "";
  }
}

/**
 * Defaults from `.env` / `.env.local` (ignored by git). localStorage overrides when non-empty.
 */
export function getKonekoBEnvDefaults() {
  const unit = (v) =>
    String(v || "").toLowerCase() === "minutes" ? "minutes" : "hours";
  return {
    gitlabToken: envPublic("VUE_APP_KONEKO_B_GITLAB_TOKEN"),
    gitlabBaseUrl:
      envPublic("VUE_APP_KONEKO_B_GITLAB_BASE_URL") || GITLAB_BASE_DEFAULT,
    token: envPublic("VUE_APP_KONEKO_B_ASANA_TOKEN"),
    projectGids: envPublic("VUE_APP_KONEKO_B_ASANA_PROJECT_GIDS"),
    workspaceGid: envPublic("VUE_APP_KONEKO_B_ASANA_WORKSPACE_GID"),
    assigneeGid: envPublic("VUE_APP_KONEKO_B_ASANA_ASSIGNEE_GID") || "me",
    asanaEstimateFieldGid: envPublic(
      "VUE_APP_KONEKO_B_ASANA_ESTIMATE_FIELD_GID"
    ),
    asanaEstimateFieldName: envPublic(
      "VUE_APP_KONEKO_B_ASANA_ESTIMATE_FIELD_NAME"
    ),
    asanaEstimateUnit: unit(envPublic("VUE_APP_KONEKO_B_ASANA_ESTIMATE_UNIT")),
    asanaSpentFieldGid: envPublic("VUE_APP_KONEKO_B_ASANA_SPENT_FIELD_GID"),
    asanaSpentFieldName: envPublic("VUE_APP_KONEKO_B_ASANA_SPENT_FIELD_NAME"),
    asanaSpentUnit: unit(envPublic("VUE_APP_KONEKO_B_ASANA_SPENT_UNIT")),
  };
}

/** Prefer stored value when non-empty; otherwise env default. */
function pickStoredOrEnv(stored, envVal, fallback = "") {
  const s =
    stored === undefined || stored === null ? "" : String(stored).trim();
  if (s !== "") return s;
  const e =
    envVal === undefined || envVal === null ? "" : String(envVal).trim();
  if (e !== "") return e;
  return fallback;
}

/** Remove browser-stored Koneko B config so `readAsanaConfig()` falls back to `.env` only. */
export function clearSavedKonekoBConfig() {
  try {
    localStorage.removeItem(ASANA_CONFIG_KEY);
  } catch {
    /* ignore */
  }
}

export function saveAsanaConfig(config) {
  const unit = (u) =>
    String(u || "").toLowerCase() === "minutes" ? "minutes" : "hours";
  const safe = {
    gitlabToken: String(config?.gitlabToken || "").trim(),
    gitlabBaseUrl: String(config?.gitlabBaseUrl || GITLAB_BASE_DEFAULT).trim(),
    token: String(config?.token || "").trim(),
    projectGids: String(config?.projectGids || "").trim(),
    workspaceGid: String(config?.workspaceGid || "").trim(),
    assigneeGid: String(config?.assigneeGid || "me").trim() || "me",
    asanaEstimateFieldGid: String(config?.asanaEstimateFieldGid || "").trim(),
    asanaEstimateFieldName: String(config?.asanaEstimateFieldName || "").trim(),
    asanaEstimateUnit: unit(config?.asanaEstimateUnit),
    asanaSpentFieldGid: String(config?.asanaSpentFieldGid || "").trim(),
    asanaSpentFieldName: String(config?.asanaSpentFieldName || "").trim(),
    asanaSpentUnit: unit(config?.asanaSpentUnit),
  };
  localStorage.setItem(ASANA_CONFIG_KEY, JSON.stringify(safe));
  return safe;
}

export function readAsanaConfig() {
  const c = getAsanaConfig();
  const defaults = getKonekoBEnvDefaults();
  /** @param {unknown} stored @param {'hours'|'minutes'} envU */
  const coerceUnit = (stored, envU) =>
    String(pickStoredOrEnv(stored, envU, "hours") || "").toLowerCase() ===
    "minutes"
      ? "minutes"
      : "hours";

  return {
    gitlabToken: pickStoredOrEnv(c.gitlabToken, defaults.gitlabToken),
    gitlabBaseUrl: pickStoredOrEnv(
      c.gitlabBaseUrl,
      defaults.gitlabBaseUrl,
      GITLAB_BASE_DEFAULT
    ),
    token: pickStoredOrEnv(c.token, defaults.token),
    projectGids: pickStoredOrEnv(
      c.projectGids || c.projectGid,
      defaults.projectGids
    ),
    workspaceGid: pickStoredOrEnv(c.workspaceGid, defaults.workspaceGid),
    assigneeGid:
      pickStoredOrEnv(c.assigneeGid, defaults.assigneeGid, "me") || "me",
    asanaEstimateFieldGid: pickStoredOrEnv(
      c.asanaEstimateFieldGid,
      defaults.asanaEstimateFieldGid
    ),
    asanaEstimateFieldName: pickStoredOrEnv(
      c.asanaEstimateFieldName,
      defaults.asanaEstimateFieldName
    ),
    asanaEstimateUnit: coerceUnit(
      c.asanaEstimateUnit,
      defaults.asanaEstimateUnit
    ),
    asanaSpentFieldGid: pickStoredOrEnv(
      c.asanaSpentFieldGid,
      defaults.asanaSpentFieldGid
    ),
    asanaSpentFieldName: pickStoredOrEnv(
      c.asanaSpentFieldName,
      defaults.asanaSpentFieldName
    ),
    asanaSpentUnit: coerceUnit(c.asanaSpentUnit, defaults.asanaSpentUnit),
  };
}

/**
 * Workspaces the PAT can access (for UI picker + auto-pick when there is only one).
 * @param {string} token
 * @returns {Promise<{gid: string, name: string}[]>}
 */
export async function fetchAsanaWorkspaceList(token) {
  const t = String(token || "").trim();
  if (!t) return [];
  const params = new URLSearchParams({
    opt_fields: "workspaces.gid,workspaces.name",
  });
  const url = `${ASANA_BASE}/users/me?${params.toString()}`;
  const res = await fetch(url, {headers: withAsanaHeaders(t)});
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`Asana users/me ${res.status}: ${body.slice(0, 180)}`);
  }
  const payload = await res.json();
  const ws = payload?.data?.workspaces;
  if (!Array.isArray(ws)) return [];
  return ws
    .map((w) => ({
      gid: String(w.gid || ""),
      name: String(w.name || "Workspace").trim() || "Workspace",
    }))
    .filter((w) => w.gid);
}

/**
 * If Asana has a single workspace, set it. If project GIDs are empty, list projects and auto-map known NekoHR names.
 * Reads current local config; does not persist — caller should saveAsanaConfig(result.cfg).
 *
 * @returns {Promise<{ cfg: ReturnType<readAsanaConfig>, workspaces: {gid: string, name: string}[], didMapProjects: boolean }>}
 */
export async function ensureAsanaConnectionPrimed() {
  /** @type {ReturnType<readAsanaConfig>} */
  let next = readAsanaConfig();
  let workspaces = [];
  let didMapProjects = false;

  if (!String(next.token || "").trim()) {
    return {cfg: next, workspaces, didMapProjects};
  }

  workspaces = await fetchAsanaWorkspaceList(next.token);

  if (!String(next.workspaceGid || "").trim() && workspaces.length === 1) {
    next = {...next, workspaceGid: workspaces[0].gid};
  }

  const gids = parseProjectGids(next.projectGids);
  if (String(next.workspaceGid || "").trim() && gids.length === 0) {
    const projects = await fetchAsanaProjects(next);
    const mapped = projects
      .filter((p) => isKnownAsanaProjectName(p.canonicalName))
      .map((p) => p.gid);
    if (mapped.length) {
      next = {...next, projectGids: mapped.join(",")};
      didMapProjects = true;
    }
  }

  return {cfg: next, workspaces, didMapProjects};
}

export async function fetchAsanaProjects(config = null) {
  const cfg = config || readAsanaConfig();
  if (!cfg.token) throw new Error("Missing Asana token");
  if (!cfg.workspaceGid)
    throw new Error("Workspace GID is required to list projects");

  const params = new URLSearchParams({
    opt_fields: "gid,name,archived,team.name",
    limit: "100",
  });
  const url = `${ASANA_BASE}/workspaces/${encodeURIComponent(
    cfg.workspaceGid
  )}/projects?${params.toString()}`;
  const res = await fetch(url, {headers: withAsanaHeaders(cfg.token)});
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`Asana projects ${res.status}: ${body.slice(0, 180)}`);
  }
  const payload = await res.json();
  const rows = Array.isArray(payload.data) ? payload.data : [];
  return rows
    .filter((p) => !p.archived)
    .map((p) => ({
      gid: String(p.gid),
      name: String(p.name || ""),
      canonicalName: canonicalizeAsanaProjectName(p.name || ""),
      teamName: p?.team?.name || "",
    }));
}

/**
 * Pull real GitLab-like issue data from management-bot report endpoint.
 * Falls back to [] if unavailable.
 */
export async function fetchGitLabTasks(ctx = {}) {
  const cfg = readAsanaConfig();
  const base = String(cfg.gitlabBaseUrl || GITLAB_BASE_DEFAULT).replace(
    /\/+$/,
    ""
  );
  const token = String(cfg.gitlabToken || "").trim();
  if (token) {
    const url = `${base}/api/v4/issues?scope=assigned_to_me&state=opened&per_page=100`;
    // Labels power NEEDS_* tool chips (management-bot parity).
    const res = await fetch(url, {
      headers: {"PRIVATE-TOKEN": token},
    });
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      throw new Error(`GitLab API ${res.status}: ${body.slice(0, 180)}`);
    }
    const rows = await res.json();
    const issues = Array.isArray(rows) ? rows : [];
    return issues.map((i) => {
      const spentSecs = Number(i?.time_stats?.total_time_spent || 0);
      const estimateSecs = Number(i?.time_stats?.time_estimate || 0);
      const assigneeName =
        i?.assignee?.name || i?.author?.name || ctx.fullName || "Unknown";
      const assigneeKey = String(
        i?.assignee?.username || ctx.username || "unknown"
      ).toLowerCase();
      const projectLabel =
        (i.references &&
          i.references.full &&
          String(i.references.full).split("#")[0]) ||
        String(i.project_id || "project");
      const gitlabLabels = Array.isArray(i.labels)
        ? i.labels.map((l) => String(l))
        : [];
      return {
        source: "gitlab",
        sourceTaskId: `gitlab:${i.project_id}:${i.iid}`,
        externalUrl: i.web_url || "",
        title: i.title || "Untitled issue",
        assigneeKey,
        assigneeLabel: assigneeName,
        projectKey: String(i.project_id || "unknown"),
        projectLabel,
        labels: gitlabLabels,
        status: i.state || "opened",
        estimateMinutes: Number.isFinite(estimateSecs)
          ? Math.round(estimateSecs / 60)
          : null,
        spentMinutes: Number.isFinite(spentSecs)
          ? Math.round(spentSecs / 60)
          : null,
        dueDate: i.due_date || null,
        milestoneDueDate: i.milestone?.due_date
          ? String(i.milestone.due_date).slice(0, 10)
          : null,
        updatedAt: i.updated_at || nowIso(),
        description: gitlabDescriptionToPlain(i.description),
      };
    });
  }

  const assigneeKey = String(ctx.username || "").toLowerCase();
  const assigneeLabel = ctx.fullName || assigneeKey;
  const mappedMember = mapNekoHRUserToManagementBotMember(assigneeKey);
  if (!mappedMember) return [];
  const url = `${MBOT_BASE}/individual/report?member=${mappedMember}&as_json=True`;
  const response = await fetch(url);
  if (!response.ok) return [];
  const json = await response.json();
  const issues = Array.isArray(json.issues) ? json.issues : [];
  return issues.map((issue) =>
    mapManagementBotIssue(issue, assigneeKey, assigneeLabel || json.name)
  );
}

/**
 * Pull tasks directly from Asana API (local-only PAT for admin experimentation).
 * Requires local config set from Koneko B UI.
 * @returns {Promise<CanonicalTask[]>}
 */
export async function fetchAsanaTasks() {
  const cfg = readAsanaConfig();
  if (!cfg.token) return [];
  const baseParams = new URLSearchParams({
    completed_since: "now",
    opt_fields:
      "gid,name,permalink_url,resource_subtype,due_on,modified_at,assignee.name,assignee.gid,projects.gid,projects.name,tags.gid,tags.name,notes,memberships.section.name," +
      "custom_fields.gid,custom_fields.name,custom_fields.resource_subtype,custom_fields.type,custom_fields.number_value,custom_fields.text_value,custom_fields.display_value",
    limit: "100",
  });

  const headers = {Authorization: `Bearer ${cfg.token}`};
  const projectGids = parseProjectGids(cfg.projectGids);
  let rows = [];

  if (projectGids.length > 0) {
    const projectCalls = await Promise.all(
      projectGids.map(async (gid) => {
        const params = new URLSearchParams(baseParams);
        const url = `${ASANA_BASE}/projects/${encodeURIComponent(
          gid
        )}/tasks?${params.toString()}`;
        const res = await fetch(url, {headers});
        if (!res.ok) {
          const body = await res.text().catch(() => "");
          throw new Error(
            `Asana project ${gid} ${res.status}: ${body.slice(0, 180)}`
          );
        }
        const payload = await res.json();
        let rows = Array.isArray(payload.data) ? payload.data : [];
        const wantedAssignee = String(cfg.assigneeGid || "me").trim();
        if (wantedAssignee && wantedAssignee !== "me") {
          rows = rows.filter(
            (t) => String(t?.assignee?.gid || "") === wantedAssignee
          );
        }
        return rows;
      })
    );
    rows = projectCalls.flat();
  } else {
    // Asana now requires explicit scoping for list calls.
    if (!cfg.workspaceGid) {
      return [];
    }
    const params = new URLSearchParams(baseParams);
    params.set("assignee", cfg.assigneeGid || "me");
    params.set("workspace", cfg.workspaceGid);
    const url = `${ASANA_BASE}/tasks?${params.toString()}`;
    const res = await fetch(url, {headers});
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      throw new Error(`Asana API ${res.status}: ${body.slice(0, 180)}`);
    }
    const payload = await res.json();
    rows = Array.isArray(payload.data) ? payload.data : [];
  }

  const deduped = new Map();
  rows.forEach((t) => {
    if (t && t.gid) deduped.set(String(t.gid), t);
  });

  return (
    Array.from(deduped.values())
      .map((t) => {
        const rawProjectName = t.projects?.[0]?.name || "";
        const canonicalProject = canonicalizeAsanaProjectName(rawProjectName);
        const asanaTags = Array.isArray(t.tags)
          ? t.tags
              .map((tg) => ({
                gid: String(tg.gid || ""),
                name: String(tg.name || "").trim(),
              }))
              .filter((x) => x.gid && x.name)
          : [];
        const tagLabels = asanaTags.map((x) => x.name);
        const notesPlain = String(t.notes || "").trim();
        const {
          estimateMinutes: estCf,
          spentMinutes: spentCf,
        } = workloadMinutesFromAsanaTask(t, cfg);
        let estimateMinutes = estCf;
        const embeddedEst = parseEmbeddedEstimateMinutes(
          t.name || "",
          notesPlain
        );
        if (
          (estimateMinutes === null ||
            !Number.isFinite(estimateMinutes) ||
            estimateMinutes <= 0) &&
          embeddedEst != null
        ) {
          estimateMinutes = embeddedEst;
        }
        return {
          source: "asana",
          sourceTaskId: `asana:${t.gid}`,
          externalUrl: t.permalink_url || `https://app.asana.com/0/${t.gid}`,
          title: t.name || "Untitled task",
          assigneeKey: String(t.assignee?.name || "asana-user")
            .toLowerCase()
            .replace(/\s+/g, "_"),
          assigneeLabel: t.assignee?.name || "Asana User",
          projectKey: canonicalProject,
          projectLabel: canonicalProject || "Asana",
          labels: tagLabels,
          asanaTags,
          status: t.resource_subtype === "milestone" ? "milestone" : "todo",
          estimateMinutes,
          spentMinutes: spentCf,
          dueDate: t.due_on || null,
          milestoneDueDate: null,
          updatedAt: t.modified_at || nowIso(),
          description: notesPlain || null,
        };
      })
      // Per request: ignore projects without a clear NekoHR home.
      .filter((row) => isKnownAsanaProjectName(row.projectLabel))
  );
}

/**
 * Merge task streams into one sorted feed.
 * @returns {Promise<CanonicalTask[]>}
 */
export async function fetchCanonicalTaskFeed(ctx = {}) {
  const [gitlab, asana] = await Promise.all([
    fetchGitLabTasks(ctx),
    fetchAsanaTasks(),
  ]);
  return [...gitlab, ...asana].sort((a, b) =>
    String(b.updatedAt).localeCompare(String(a.updatedAt))
  );
}

export function summarizeByAssignee(tasks) {
  const out = {};
  (tasks || []).forEach((t) => {
    const k = t.assigneeKey || "unknown";
    if (!out[k]) {
      out[k] = {
        assigneeKey: k,
        assigneeLabel: t.assigneeLabel || k,
        count: 0,
        estimateMinutes: 0,
        spentMinutes: 0,
      };
    }
    out[k].count += 1;
    out[k].estimateMinutes += Number.isFinite(t.estimateMinutes)
      ? t.estimateMinutes
      : 0;
    out[k].spentMinutes += Number.isFinite(t.spentMinutes) ? t.spentMinutes : 0;
  });
  return Object.values(out).sort((a, b) => b.count - a.count);
}

// ——— Write-back helpers (GitLab + Asana APIs) ———

/** @param {string} sourceTaskId */
export function parseGitlabTaskRef(sourceTaskId) {
  const m = String(sourceTaskId || "").match(/^gitlab:(\d+):(\d+)$/);
  if (!m) return null;
  return {projectId: m[1], iid: m[2]};
}

/** @param {string} sourceTaskId */
export function parseAsanaTaskGid(sourceTaskId) {
  const m = String(sourceTaskId || "").match(/^asana:(.+)$/);
  if (!m) return null;
  return m[1];
}

/**
 * @param {object} patch
 * @param {string} patch.sourceTaskId
 * @param {string|null|undefined} [patch.dueDate] — omit to skip; use null or "" to clear
 * @param {string[]|undefined} [patch.labels] — full issue label set (GitLab)
 * @param {number|undefined} [patch.timeEstimateSeconds]
 */
export async function patchGitLabIssue(patch) {
  const cfg = readAsanaConfig();
  const ref = parseGitlabTaskRef(patch.sourceTaskId);
  if (!ref) throw new Error("Not a GitLab-backed task id");
  const token = String(cfg.gitlabToken || "").trim();
  if (!token)
    throw new Error("GitLab token missing (Connections → GitLab PAT)");
  const base = String(cfg.gitlabBaseUrl || GITLAB_BASE_DEFAULT).replace(
    /\/+$/,
    ""
  );
  const url = `${base}/api/v4/projects/${encodeURIComponent(
    ref.projectId
  )}/issues/${encodeURIComponent(ref.iid)}`;

  /** @type {Record<string, unknown>} */
  const body = {};
  if (patch.dueDate !== undefined) {
    const d = patch.dueDate;
    body.due_date = d && String(d).trim() ? String(d).slice(0, 10) : null;
  }
  if (Array.isArray(patch.labels)) {
    body.labels = patch.labels.join(",");
  }
  if (patch.timeEstimateSeconds !== undefined) {
    const n = Number(patch.timeEstimateSeconds);
    body.time_estimate = Number.isFinite(n) && n >= 0 ? Math.round(n) : 0;
  }

  const res = await fetch(url, {
    method: "PUT",
    headers: {"PRIVATE-TOKEN": token, "Content-Type": "application/json"},
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const bodyText = await res.text().catch(() => "");
    throw new Error(`GitLab update ${res.status}: ${bodyText.slice(0, 240)}`);
  }
}

/**
 * @param {ReturnType<readAsanaConfig>} cfg
 * @param {string} gid
 * @param {Record<string, unknown>} data — Asana `data` object (e.g. `{ due_on, notes, name }`)
 */
async function asanaPutTaskData(cfg, gid, data) {
  const token = String(cfg.token || "").trim();
  if (!token) throw new Error("Asana token missing");
  const url = `${ASANA_BASE}/tasks/${encodeURIComponent(gid)}`;
  const res = await fetch(url, {
    method: "PUT",
    headers: {...withAsanaHeaders(token), "Content-Type": "application/json"},
    body: JSON.stringify({data}),
  });
  if (!res.ok) {
    const bodyText = await res.text().catch(() => "");
    throw new Error(
      `Asana task update ${res.status}: ${bodyText.slice(0, 240)}`
    );
  }
}

/**
 * @param {ReturnType<readAsanaConfig>} cfg
 * @param {string} taskGid
 * @param {string} subPath — `addTag` | `removeTag`
 * @param {Record<string, unknown>} data
 */
async function asanaTaskTagMutation(cfg, taskGid, subPath, data) {
  const token = String(cfg.token || "").trim();
  if (!token) throw new Error("Asana token missing");
  const url = `${ASANA_BASE}/tasks/${encodeURIComponent(taskGid)}/${subPath}`;
  const res = await fetch(url, {
    method: "POST",
    headers: {...withAsanaHeaders(token), "Content-Type": "application/json"},
    body: JSON.stringify({data}),
  });
  if (!res.ok) {
    const bodyText = await res.text().catch(() => "");
    throw new Error(
      `Asana ${subPath} ${res.status}: ${bodyText.slice(0, 240)}`
    );
  }
}

/**
 * @param {ReturnType<readAsanaConfig>} cfg
 * @param {string} workspaceGid
 * @param {string} name
 */
async function findOrCreateWorkspaceTagGid(cfg, workspaceGid, name) {
  const token = String(cfg.token || "").trim();
  const qs = new URLSearchParams({limit: "100", opt_fields: "gid,name"});
  const url = `${ASANA_BASE}/workspaces/${encodeURIComponent(
    workspaceGid
  )}/tags?${qs.toString()}`;
  const res = await fetch(url, {headers: withAsanaHeaders(token)});
  if (res.ok) {
    const payload = await res.json();
    const rows = Array.isArray(payload.data) ? payload.data : [];
    const hit = rows.find((t) => String(t.name || "") === name);
    if (hit?.gid) return String(hit.gid);
  }
  const createUrl = `${ASANA_BASE}/workspaces/${encodeURIComponent(
    workspaceGid
  )}/tags`;
  const cr = await fetch(createUrl, {
    method: "POST",
    headers: {...withAsanaHeaders(token), "Content-Type": "application/json"},
    body: JSON.stringify({data: {name}}),
  });
  if (!cr.ok) {
    const bodyText = await cr.text().catch(() => "");
    throw new Error(`Asana create tag ${cr.status}: ${bodyText.slice(0, 240)}`);
  }
  const created = await cr.json();
  const gid = created?.data?.gid;
  if (!gid) throw new Error("Asana tag create returned no gid");
  return String(gid);
}

/**
 * Mirrors GitLab `Priority: *` labels onto Asana workspace tags of the same name (creates tag if missing).
 * @param {ReturnType<readAsanaConfig>} cfg
 * @param {string} taskGid
 * @param {string} workspaceGid
 * @param {string} priorityLabelOrEmpty
 * @param {{gid: string, name: string}[]} [currentTags]
 */
export async function syncAsanaTaskPriorityTags(
  cfg,
  taskGid,
  workspaceGid,
  priorityLabelOrEmpty,
  currentTags
) {
  const want = String(priorityLabelOrEmpty || "").trim();

  for (let i = 0; i < GITLAB_STYLE_PRIORITY_LABELS.length; i += 1) {
    const label = GITLAB_STYLE_PRIORITY_LABELS[i];
    const hit = (currentTags || []).find((t) => t.name === label);
    if (hit?.gid) {
      await asanaTaskTagMutation(cfg, taskGid, "removeTag", {tag: hit.gid});
    }
  }

  if (!want || !GITLAB_STYLE_PRIORITY_LABELS.includes(want)) {
    return;
  }

  const tagGid = await findOrCreateWorkspaceTagGid(cfg, workspaceGid, want);
  await asanaTaskTagMutation(cfg, taskGid, "addTag", {tag: tagGid});
}

/**
 * Updates Asana estimate: custom field when configured, otherwise `(Est: Xh)` in task notes (optional `[Est: …]` in title).
 * @param {import("./canonicalTaskFeed").CanonicalTask & {description?: string}} task
 * @param {number} minutes
 * @param {ReturnType<readAsanaConfig>} cfg
 * @param {{ mirrorEstInTitle?: boolean }} [opts]
 */
export async function pushAsanaEstimateToService(
  task,
  minutes,
  cfg,
  opts = {}
) {
  const gid = parseAsanaTaskGid(task.sourceTaskId);
  if (!gid) throw new Error("Not an Asana task");

  const m = Number(minutes);
  if (!Number.isFinite(m) || m < 0) throw new Error("Invalid estimate");

  const fieldGid = String(cfg.asanaEstimateFieldGid || "").trim();
  if (fieldGid) {
    const unit = cfg.asanaEstimateUnit === "minutes" ? "minutes" : "hours";
    const val =
      unit === "minutes" ? Math.round(m) : Math.round((m / 60) * 100) / 100;
    await asanaPutTaskData(cfg, gid, {custom_fields: {[fieldGid]: val}});
    return;
  }

  const hours = m / 60;
  const notes = mergeEstimateIntoAsanaNotes(task.description || "", hours);
  /** @type {Record<string, unknown>} */
  const data = {notes};
  if (opts.mirrorEstInTitle) {
    const hLabel = Number.isInteger(hours * 100)
      ? String(Math.round(hours * 100) / 100)
      : hours.toFixed(2).replace(/\.?0+$/, "");
    const baseTitle = stripEmbeddedEstimateFromTitle(task.title || "");
    data.name = `${baseTitle} [Est: ${hLabel}h]`.trim();
  }
  await asanaPutTaskData(cfg, gid, data);
}

/** Push GitLab `time_estimate` from whole minutes (maps to seconds for the REST API). */
export async function pushGitLabEstimateToService(task, minutes) {
  const cfg = readAsanaConfig();
  if (!String(cfg.gitlabToken || "").trim()) {
    throw new Error("GitLab token missing");
  }
  const mins = Number(minutes);
  if (!Number.isFinite(mins) || mins < 0) throw new Error("Invalid estimate");
  const seconds = Math.round(mins * 60);
  await patchGitLabIssue({
    sourceTaskId: task.sourceTaskId,
    timeEstimateSeconds: seconds,
  });
}

/**
 * @param {ReturnType<readAsanaConfig>} cfg
 * @param {import("./canonicalTaskFeed").CanonicalTask & {asanaTags?: {gid: string, name: string}[]}} task
 * @param {{ dueDate?: string, priorityLabel?: string }} draft — set only fields to update; `dueDate` "" clears; `priorityLabel` "" removes priority label/tags
 */
export async function pushTaskDueAndPriorityToService(cfg, task, draft) {
  if (task.source === "gitlab") {
    /** @type {Parameters<typeof patchGitLabIssue>[0]} */
    const p = {sourceTaskId: task.sourceTaskId};
    if (draft.dueDate !== undefined) {
      const dueRaw = String(draft.dueDate || "").trim();
      p.dueDate = dueRaw === "" ? null : dueRaw.slice(0, 10);
    }
    if (draft.priorityLabel !== undefined) {
      p.labels = mergeGitLabStylePriorityIntoLabels(
        task.labels || [],
        draft.priorityLabel || ""
      );
    }
    await patchGitLabIssue(p);
    return;
  }

  if (task.source !== "asana") {
    throw new Error("Unsupported task source");
  }

  const gid = parseAsanaTaskGid(task.sourceTaskId);
  if (!gid) throw new Error("Bad Asana id");
  const ws = String(cfg.workspaceGid || "").trim();
  if (!ws) throw new Error("Asana workspace required for priority tag sync");

  if (draft.dueDate !== undefined) {
    const dueRaw = String(draft.dueDate || "").trim();
    await asanaPutTaskData(cfg, gid, {
      due_on: dueRaw ? dueRaw.slice(0, 10) : null,
    });
  }
  if (draft.priorityLabel !== undefined) {
    await syncAsanaTaskPriorityTags(
      cfg,
      gid,
      ws,
      draft.priorityLabel || "",
      task.asanaTags || []
    );
  }
}
